import { Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "nazare-ui/utils";

import styles from "./header-and-stats.module.css";
import { useSecondaryHeader } from "../../../secondary-header-context";
import { SkeletonBlock } from "../../../skeleton-block";
import { useSimulatedLoad } from "../../../use-simulated-load";

type WindowKey = "today" | "7d" | "30d" | "6m" | "all";

type WindowOption = {
  key: WindowKey;
  label: string;
};

const WINDOW_OPTIONS: WindowOption[] = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "6m", label: "Last 6 months" },
  { key: "all", label: "All time" },
];

type StatAccent = "green" | "orange" | "red" | "neutral";

type StatCardData = {
  key: string;
  label: string;
  value: string;
  allTime: string;
  accent: StatAccent;
};

// Mock KPI numbers for the "Last 30 days" window — this bootstrap has no
// live backend, so the same static figures are shown regardless of which
// date-range tab is active (only the label's window suffix changes).
// Shaped after the real dashboard at /admin/qa-automation.
const STAT_CARDS: StatCardData[] = [
  {
    key: "pass-rate",
    label: "Pass rate",
    value: "78.5%",
    allTime: "All time: 69.7%",
    accent: "green",
  },
  {
    key: "pass-with-comments",
    label: "Pass w/ comments",
    value: "19.4%",
    allTime: "All time: 16.9%",
    accent: "orange",
  },
  {
    key: "fail-rate",
    label: "Fail rate",
    value: "2.0%",
    allTime: "All time: 13.4%",
    accent: "red",
  },
  {
    key: "runs",
    label: "Runs",
    value: "34",
    allTime: "All time: 76",
    accent: "neutral",
  },
];

const ACCENT_CLASS: Record<StatAccent, string> = {
  green: styles.accentGreen,
  orange: styles.accentOrange,
  red: styles.accentRed,
  neutral: styles.accentNeutral,
};

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

// Fixed at module load — this is static mock data (no live schedule
// backend), so "now" only needs to be stable for one render pass, not
// continuously ticking.
const NOW = Date.now();

/** Mock upcoming scheduled runs: soonest first. */
const NEXT_SCHEDULED_RUNS = [
  new Date(NOW + 14 * HOUR_MS).toISOString(),
  new Date(NOW + 1 * DAY_MS + 2 * HOUR_MS).toISOString(),
  new Date(NOW + 3 * DAY_MS + 6 * HOUR_MS).toISOString(),
];

const RELATIVE_TIME = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

/** "in 14 hours" / "in 3 days" style relative label for a mock ISO timestamp. */
function formatRelative(iso: string): string {
  const diffMs = new Date(iso).getTime() - NOW;
  const diffHours = Math.round(diffMs / HOUR_MS);
  if (Math.abs(diffHours) < 24) {
    return RELATIVE_TIME.format(diffHours, "hour");
  }
  const diffDays = Math.round(diffMs / DAY_MS);
  return RELATIVE_TIME.format(diffDays, "day");
}

/** "7 Jul 2026, 14:32" style absolute UTC label for a mock ISO timestamp. */
function formatUtc(iso: string): string {
  return `${new Date(iso).toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  })} UTC`;
}

/**
 * "Next scheduled run" / "Runs in progress" info cards, and the four
 * pass/comment/fail/runs KPI cards from the top of the real
 * `/admin/qa-automation` dashboard. The date-range tab bar + "Start new run"
 * action that used to render above these is now published into the app
 * shell's secondary-header slot instead (see `useSecondaryHeader` below).
 * Charts and tables below this belong to sibling components. All figures
 * here are static mock data — there is no live QA-run backend in this app.
 */
export function QaAutomationHeaderAndStats() {
  const isLoading = useSimulatedLoad();
  const [activeWindow, setActiveWindow] = useState<WindowKey>("30d");

  const activeWindowLabel = useMemo(
    () => WINDOW_OPTIONS.find((option) => option.key === activeWindow)?.label ?? "",
    [activeWindow],
  );

  const tabBarRow = useMemo(
    () => (
      <div className={styles.tabBarRow}>
        <div className={styles.tabBar} role="tablist" aria-label="Date range">
          {WINDOW_OPTIONS.map((option) => {
            const active = option.key === activeWindow;
            return (
              <button
                key={option.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={cn(styles.tab, active && styles.tabActive)}
                onClick={() => setActiveWindow(option.key)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <span className={styles.spacer} />
        <button type="button" className={styles.startRunButton}>
          <Plus size={14} aria-hidden />
          Start new run
        </button>
      </div>
    ),
    [activeWindow, setActiveWindow],
  );

  useSecondaryHeader(tabBarRow);

  const nextRun = NEXT_SCHEDULED_RUNS[0];
  const laterRuns = NEXT_SCHEDULED_RUNS.slice(1, 3);

  return (
    <div className={styles.root}>
      <div className={styles.infoRow}>
        <section className={styles.infoCard}>
          <p className={styles.infoLabel}>Next scheduled run</p>
          {isLoading ? (
            <>
              <SkeletonBlock width="58%" height={16} />
              <SkeletonBlock width="38%" height={12} className={styles.infoSecondarySkeleton} />
            </>
          ) : (
            <>
              <p className={styles.infoPrimary}>
                {formatRelative(nextRun)}
                <span className={styles.infoMuted}> · {formatUtc(nextRun)}</span>
              </p>
              {laterRuns.length > 0 && (
                <p className={styles.infoSecondary}>
                  Then:{" "}
                  {laterRuns.map((iso, index) => (
                    <span key={iso}>
                      {index > 0 && ", "}
                      {formatRelative(iso)}
                    </span>
                  ))}
                </p>
              )}
            </>
          )}
        </section>

        <section className={styles.infoCard}>
          <p className={styles.infoLabel}>Runs in progress</p>
          {isLoading ? (
            <SkeletonBlock width="44%" height={12} className={styles.infoSecondarySkeleton} />
          ) : (
            <p className={styles.infoSecondary}>No runs in progress.</p>
          )}
        </section>
      </div>

      <div className={styles.statRow}>
        {STAT_CARDS.map((stat, index) => (
          <section
            key={stat.key}
            className={cn(styles.statCard, ACCENT_CLASS[stat.accent])}
          >
            <p className={styles.statLabel}>
              {stat.label} · {activeWindowLabel}
            </p>
            {isLoading ? (
              <>
                <SkeletonBlock
                  width={`${58 + ((index * 13) % 30)}%`}
                  height={28}
                  className={styles.statValueSkeleton}
                />
                <SkeletonBlock
                  width={`${45 + ((index * 17) % 35)}%`}
                  height={12}
                  className={styles.statAllTimeSkeleton}
                />
              </>
            ) : (
              <>
                <p className={styles.statValue}>{stat.value}</p>
                <p className={styles.statAllTime}>{stat.allTime}</p>
              </>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
